import { extendCanvasDataWithMetricContentSize } from '@configuratorware/configurator-frontendgui/App/Reducers/DesignData/Modifiers';
import {
  createColorData,
  hexToRgb,
} from '@configuratorware/configurator-frontendgui/App/Reducers/DesignArea/Selectors';
import type { FontRecord } from './fonts';
import { findFontByFamily, getFontFamilyName } from './fonts';
import { formatHexColor } from './editorUnits';
import type { ColorArea, EditorItem, EditorState, ImageItem, TextItem } from '../Reducers/Configurator/types';

export const DEFAULT_DESIGN_AREA_IDENTIFIER = 'default';

/** DPI is unused by the API; 0 keeps PDF display values in millimeters. */
export const DESIGN_DATA_DPI = 0;

type ColorStyleEntry = {
  identifier: string;
  value: string;
};

export function buildVectorizeColorsMap(
  colorAreas: ColorArea[],
  paletteIdentifier = 'default'
): Record<string, ColorStyleEntry> {
  const map: Record<string, ColorStyleEntry> = {};
  colorAreas.forEach((area) => {
    if (!area.colorOptionId || map[area.colorOptionId]) return;
    const value = hexToRgb(formatHexColor(area.color)) || area.color;
    map[area.colorOptionId] = createColorData(area.colorOptionId, paletteIdentifier, false, value);
  });
  return map;
}

function resolveTextColorStyle(item: TextItem, paletteIdentifier = 'default'): ColorStyleEntry {
  const value = hexToRgb(formatHexColor(item.color)) || item.color;
  const colorId = item.colorOptionId || 'black';
  return createColorData(colorId, paletteIdentifier, false, value);
}

export type DesignAreaData = {
  canvasData: {
    objects: DesignDataCanvasObject[];
    size: { width: number; height: number; dpi: number };
    metricContentSize?: { width: number; height: number };
  } | null;
  designProductionMethodIdentifier: string;
  colorAmount: number;
};

export type DesignData = Record<string, DesignAreaData>;

export type BuildDesignDataContext = {
  canvasWidthMm: number;
  canvasHeightMm: number;
  fonts?: FontRecord[];
  fontSizeMin?: number;
  designProductionMethodIdentifier?: string;
  colorAmount?: number;
  colorPaletteIdentifier?: string;
};

function centerFromTopLeftMm(xMm: number, yMm: number, widthMm: number, heightMm: number) {
  return {
    x: xMm + widthMm / 2,
    y: yMm + heightMm / 2,
  };
}

function metricFromTopLeftMm(xMm: number, yMm: number, widthMm: number, heightMm: number) {
  const center = centerFromTopLeftMm(xMm, yMm, widthMm, heightMm);
  return {
    x: center.x,
    y: center.y,
    width: widthMm,
    height: heightMm,
  };
}

type DesignDataCanvasObject = Record<string, unknown>;

function buildFontItemRecord(font: FontRecord | null): Record<string, unknown> {
  if (!font) {
    return {
      name: 'Roboto',
      family: 'Roboto',
      regular: true,
      bold: false,
      italic: false,
      boldItalic: false,
      isDefault: null,
    };
  }

  return {
    name: font.name,
    family: getFontFamilyName(font),
    regular: font.regular ?? true,
    bold: Boolean(font.urlBold),
    italic: Boolean(font.urlItalic),
    boldItalic: Boolean(font.urlBoldItalic),
    isDefault: font.isDefault ?? null,
    ...(font.urlRegular ? { urlRegular: font.urlRegular } : {}),
    ...(font.urlBold ? { urlBold: font.urlBold } : {}),
    ...(font.urlItalic ? { urlItalic: font.urlItalic } : {}),
    ...(font.urlBoldItalic ? { urlBoldItalic: font.urlBoldItalic } : {}),
  };
}

function buildTextContent(item: TextItem, colorEntry: ColorStyleEntry, fontFamily: string): string {
  const escapedColorAttr = colorEntry.identifier.replace(/"/g, '&quot;');
  const fontParams = `font-family: ${fontFamily}; font-weight: 400; font-style: normal; text-align: left; color: ${colorEntry.value};`;
  const inner = `<span data-color="${escapedColorAttr}" style="${fontParams}">${item.content || ''}</span>`;
  return `<div data-color="${escapedColorAttr}" style="color: ${colorEntry.value}; font-size: 16px; font-family: ${fontFamily};">${inner}</div>`;
}

function buildTextCanvasObject(
  item: TextItem,
  context: BuildDesignDataContext
): DesignDataCanvasObject {
  const fontSizeMin = context.fontSizeMin ?? 6;
  const font = findFontByFamily(context.fonts || [], item.fontFamily);
  const fontFamily = font ? getFontFamilyName(font) : item.fontFamily;
  const fontItem = buildFontItemRecord(font);
  const colorEntry = resolveTextColorStyle(item, context.colorPaletteIdentifier);
  const { x, y } = centerFromTopLeftMm(item.x, item.y, item.width, item.height);
  const fontSize = Math.max(fontSizeMin, item.height);

  return {
    type: 'Text',
    uuid: item.id,
    x,
    y,
    rotation: 0,
    scaling: 1,
    locked: false,
    autoFit: false,
    userData: {},
    content: buildTextContent(item, colorEntry, fontFamily),
    style: {
      colors: [colorEntry],
      fontWeight: '400',
      fontStyle: 'normal',
      textAlign: 'left',
      fontSize,
      fontFamily,
      fontItem,
    },
    fontSize,
    fontSizeMin,
    isBulkName: false,
    textAlign: 'left',
    fontItem,
    metric: metricFromTopLeftMm(item.x, item.y, item.width, item.height),
  };
}

function buildImageCanvasObject(item: ImageItem, paletteIdentifier = 'default'): DesignDataCanvasObject | null {
  if (!item.imageData && !item.url) return null;

  const imageData = (item.imageData || {}) as {
    preview?: { width?: number; height?: number };
    operations?: { vectorizeColorsMap?: Record<string, ColorStyleEntry> };
  };
  const previewWidth = imageData.preview?.width || item.viewBoxWidth || 0;
  if (!(previewWidth > 0) && !item.url) return null;

  const scaling = previewWidth > 0 ? item.width / previewWidth : 1;
  const { x, y } = centerFromTopLeftMm(item.x, item.y, item.width, item.height);
  const vectorizeColorsMap =
    imageData.operations?.vectorizeColorsMap ||
    (item.colorAreas?.length ? buildVectorizeColorsMap(item.colorAreas, paletteIdentifier) : {});

  return {
    type: 'Image',
    uuid: item.id,
    x,
    y,
    rotation: 0,
    scaling,
    locked: false,
    autoFit: false,
    userData: {},
    src: item.url,
    imageData: {
      ...imageData,
      operations: {
        ...(imageData.operations || {}),
        vectorizeColorsMap,
      },
    },
    metric: metricFromTopLeftMm(item.x, item.y, item.width, item.height),
  };
}

function collectDistinctColors(objects: DesignDataCanvasObject[]): ColorStyleEntry[] {
  const seen = new Set<string>();
  const colors: ColorStyleEntry[] = [];

  objects.forEach((object) => {
    if (object.type === 'Text') {
      const entries = (object.style as { colors?: ColorStyleEntry[] })?.colors || [];
      entries.forEach((entry) => {
        if (!seen.has(entry.identifier)) {
          seen.add(entry.identifier);
          colors.push(entry);
        }
      });
    } else if (object.type === 'Image') {
      const map =
        (object.imageData as { operations?: { vectorizeColorsMap?: Record<string, ColorStyleEntry> } })
          ?.operations?.vectorizeColorsMap || {};
      Object.values(map).forEach((entry) => {
        if (entry?.identifier && !seen.has(entry.identifier)) {
          seen.add(entry.identifier);
          colors.push(entry);
        }
      });
    }
  });

  return colors;
}

function buildCanvasData(editor: EditorState, context: BuildDesignDataContext) {
  const items = Object.values(editor.objects);
  if (items.length === 0) return null;

  const objects: DesignDataCanvasObject[] = [];

  items.forEach((item) => {
    if (item.type === 'text') {
      objects.push(buildTextCanvasObject(item, context));
      return;
    }
    const imageObject = buildImageCanvasObject(item, context.colorPaletteIdentifier);
    if (imageObject) objects.push(imageObject);
  });

  if (objects.length === 0) return null;

  const canvasData = extendCanvasDataWithMetricContentSize({
    objects,
    size: {
      width: context.canvasWidthMm,
      height: context.canvasHeightMm,
      dpi: DESIGN_DATA_DPI,
    },
  });

  return canvasData;
}

export function buildDesignDataFromEditor(
  editor: EditorState,
  context: BuildDesignDataContext,
  existing: DesignData = {}
): DesignData {
  const areaId = DEFAULT_DESIGN_AREA_IDENTIFIER;
  const existingArea = existing[areaId] || {};
  const canvasData = buildCanvasData(editor, context);
  const objects = canvasData?.objects || [];
  const colorAmount =
    context.colorAmount ??
    (objects.length > 0 ? Math.max(1, collectDistinctColors(objects).length) : existingArea.colorAmount ?? 1);

  return {
    ...existing,
    [areaId]: {
      ...existingArea,
      canvasData,
      designProductionMethodIdentifier:
        context.designProductionMethodIdentifier ||
        existingArea.designProductionMethodIdentifier ||
        'print',
      colorAmount,
    },
  };
}
